import { ThemedText } from "@/components/ThemedText";
import CountryDropdown, {
  type CountryDropdownOption,
} from "@/components/ui/CountryDropdown.web";
import { FormButton, FormLinkButton } from "@/components/ui/forms";
import { COUNTRIES } from "@/constants/countries";
import { linkPhoneProvider } from "@/utils/accountLinking.web";
import {
  startPhoneSignIn,
  takePendingPhoneSignIn,
  type PhoneSignInConfirmation,
} from "@/utils/phoneSignIn.web";
import {
  PhoneVerificationError,
  type PhoneVerificationErrorCode,
} from "@/utils/phoneVerification.web";
import type { User, UserCredential } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Linking,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

const RECAPTCHA_PRIVACY_URL = "https://policies.google.com/privacy";
const RECAPTCHA_TERMS_URL = "https://policies.google.com/terms";

function RecaptchaDisclosure() {
  const { t } = useTranslation();
  return (
    <ThemedText className="mt-4 text-xs text-center text-gray-600">
      {t("RECAPTCHA_DISCLOSURE_PREFIX")}
      <ThemedText
        className="text-xs text-blue-600 underline"
        onPress={() => Linking.openURL(RECAPTCHA_PRIVACY_URL)}
      >
        {t("RECAPTCHA_DISCLOSURE_PRIVACY")}
      </ThemedText>
      {t("RECAPTCHA_DISCLOSURE_MIDDLE")}
      <ThemedText
        className="text-xs text-blue-600 underline"
        onPress={() => Linking.openURL(RECAPTCHA_TERMS_URL)}
      >
        {t("RECAPTCHA_DISCLOSURE_TERMS")}
      </ThemedText>
      {t("RECAPTCHA_DISCLOSURE_SUFFIX")}
    </ThemedText>
  );
}

export type PhoneAuthSuccessPayload = {
  userCredential: UserCredential | null;
  phonePrefix: string;
  phoneNumber: string;
};

type PhoneAuthFlowConfirmation =
  | { kind: "signin"; inner: PhoneSignInConfirmation }
  | { kind: "link"; confirm: (code: string) => Promise<void> };

export type PhoneAuthFlowMode = "signin" | "link";

export type PhoneAuthFlowProps = {
  onSuccess: (payload: PhoneAuthSuccessPayload) => Promise<void> | void;
  onCancel: () => void;
  onSendError: (params: {
    code: PhoneVerificationErrorCode;
    defaultMessageKey: string;
  }) => void;
  subtitleKey: string;
  sendCodeKey: string;
  footer?: React.ReactNode;
  loading: boolean;
  onLoadingChange: (loading: boolean) => void;
  mode?: PhoneAuthFlowMode;
  user?: User | null;
};

const RESEND_COOLDOWN_MS = 30_000;

const computeCooldownSec = (sentAt: number | null): number => {
  if (sentAt === null) return 0;
  return Math.max(
    0,
    Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - sentAt)) / 1000),
  );
};

const defaultSendErrorKey = (code: PhoneVerificationErrorCode): string => {
  switch (code) {
    case "INVALID_PHONE":
      return "PLEASE_ENTER_VALID_PHONE_NUMBER";
    case "TOO_MANY_REQUESTS":
      return "RESEND_TOO_MANY_REQUESTS";
    case "QUOTA_EXCEEDED":
    case "SEND_FAILED":
    default:
      return "FAILED_TO_SEND_VERIFICATION_CODE";
  }
};

const defaultConfirmErrorKey = (code: PhoneVerificationErrorCode): string => {
  switch (code) {
    case "INVALID_CODE":
      return "INVALID_VERIFICATION_CODE";
    case "CODE_EXPIRED":
      return "CODE_EXPIRED_REQUEST_NEW";
    case "VERIFY_FAILED":
    default:
      return "VERIFICATION_FAILED";
  }
};

export function PhoneAuthFlow({
  onSuccess,
  onCancel,
  onSendError,
  subtitleKey,
  sendCodeKey,
  footer,
  loading,
  onLoadingChange,
  mode = "signin",
  user,
}: PhoneAuthFlowProps) {
  const { t } = useTranslation();

  // If the user previously received an SMS and closed the flow without
  // verifying (Back tap, parent re-render, page navigation), pick up on the
  // OTP step with the same confirmation instead of forcing another SMS.
  // Link mode has its own linkPhoneProvider path with no stash today.
  const [initialStash] = useState(() =>
    mode === "signin" ? takePendingPhoneSignIn() : null,
  );

  const [step, setStep] = useState<"enter-phone" | "enter-code">(
    initialStash ? "enter-code" : "enter-phone",
  );
  const [phonePrefix, setPhonePrefix] = useState(
    initialStash?.phonePrefix ?? "+595",
  );
  const [phoneNumber, setPhoneNumber] = useState(
    initialStash?.phoneNumber ?? "",
  );
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState<
    string | null
  >(null);
  const [phoneConfirmation, setPhoneConfirmation] =
    useState<PhoneAuthFlowConfirmation | null>(
      initialStash
        ? { kind: "signin", inner: initialStash.confirmation }
        : null,
    );

  // Timestamp of the most recent successful SMS send. Drives the resend
  // cooldown countdown; hydrates from the stash so switching away and back
  // preserves the remaining wait instead of restarting it.
  const [lastSentAt, setLastSentAt] = useState<number | null>(
    initialStash?.sentAt ?? null,
  );
  const [cooldownSec, setCooldownSec] = useState<number>(() =>
    computeCooldownSec(initialStash?.sentAt ?? null),
  );

  useEffect(() => {
    if (lastSentAt === null) return;
    const tick = () => {
      const remaining = computeCooldownSec(lastSentAt);
      setCooldownSec(remaining);
      return remaining;
    };
    if (tick() === 0) return;
    const id = setInterval(() => {
      if (tick() === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lastSentAt]);

  const selectedPhoneCountry = COUNTRIES.find(
    (c) => "phonePrefix" in c && c.phonePrefix === phonePrefix,
  );
  const normalizePhoneInput = (raw: string): string => {
    const digitsOnly = raw.replace(/\D/g, "");
    const checker = (selectedPhoneCountry as any)?.phoneNumberCheck;
    return typeof checker === "function" ? checker(digitsOnly) : digitsOnly;
  };
  const phoneRegex = (selectedPhoneCountry as any)?.phoneRegex as
    | RegExp
    | undefined;
  const phoneMaxLength = (selectedPhoneCountry as any)?.phoneMaxLength as
    | number
    | undefined;
  const phonePlaceholder = (selectedPhoneCountry as any)?.phonePlaceholder as
    | string
    | undefined;
  const normalizedPhone = normalizePhoneInput(phoneNumber);
  const isPhoneValid = phoneRegex
    ? phoneRegex.test(normalizedPhone)
    : normalizedPhone.length >= 6;

  const resetFlow = () => {
    setStep("enter-phone");
    setPhoneNumber("");
    setPhoneNumberError(null);
    setVerificationCode("");
    setVerificationCodeError(null);
    setPhoneConfirmation(null);
  };

  const handleCancel = () => {
    resetFlow();
    onCancel();
  };

  // Shared SMS-send path used by both the initial "Send code" tap and the
  // cooldown-gated "Resend" tap on the OTP step. Returns true on success so
  // the caller can decide whether to advance the step; on failure it routes
  // the mapped code through onSendError and returns false.
  const sendVerificationCode = async (): Promise<boolean> => {
    const phoneE164 = `${phonePrefix}${normalizedPhone}`;
    try {
      if (mode === "link") {
        if (!user) {
          onSendError({
            code: "SEND_FAILED",
            defaultMessageKey: defaultSendErrorKey("SEND_FAILED"),
          });
          return false;
        }
        const linkConfirmation = await linkPhoneProvider(user, phoneE164, {
          prefix: phonePrefix,
          number: normalizedPhone,
        });
        setPhoneConfirmation({
          kind: "link",
          confirm: linkConfirmation.confirm,
        });
      } else {
        const confirmation = await startPhoneSignIn(phoneE164, {
          phonePrefix,
          phoneNumber: normalizedPhone,
        });
        setPhoneConfirmation({ kind: "signin", inner: confirmation });
      }
      setLastSentAt(Date.now());
      return true;
    } catch (error) {
      const code: PhoneVerificationErrorCode =
        error instanceof PhoneVerificationError ? error.code : "SEND_FAILED";
      onSendError({ code, defaultMessageKey: defaultSendErrorKey(code) });
      return false;
    }
  };

  const handleSendCode = async () => {
    setPhoneNumberError(null);
    if (!phonePrefix || !isPhoneValid) {
      setPhoneNumberError(t("PLEASE_ENTER_VALID_PHONE_NUMBER"));
      return;
    }
    if (normalizedPhone !== phoneNumber) setPhoneNumber(normalizedPhone);

    onLoadingChange(true);
    try {
      const ok = await sendVerificationCode();
      if (ok) setStep("enter-code");
    } finally {
      onLoadingChange(false);
    }
  };

  const handleResend = async () => {
    if (cooldownSec > 0 || loading) return;
    setVerificationCode("");
    setVerificationCodeError(null);
    onLoadingChange(true);
    try {
      await sendVerificationCode();
    } finally {
      onLoadingChange(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerificationCodeError(null);
    if (!phoneConfirmation || verificationCode.length < 6) {
      setVerificationCodeError(t("PLEASE_ENTER_VERIFICATION_CODE"));
      return;
    }

    onLoadingChange(true);
    try {
      let userCredential: UserCredential | null = null;
      if (phoneConfirmation.kind === "signin") {
        userCredential =
          await phoneConfirmation.inner.confirm(verificationCode);
      } else {
        await phoneConfirmation.confirm(verificationCode);
      }
      await onSuccess({ userCredential, phonePrefix, phoneNumber });
    } catch (error) {
      const code: PhoneVerificationErrorCode =
        error instanceof PhoneVerificationError ? error.code : "VERIFY_FAILED";
      setVerificationCodeError(t(defaultConfirmErrorKey(code)));
    } finally {
      onLoadingChange(false);
    }
  };

  if (step === "enter-phone") {
    return (
      <>
        <ThemedText className="mb-3 text-sm text-gray-600 text-center">
          {t(subtitleKey)}
        </ThemedText>
        <ThemedText className="mb-1 text-sm text-gray-700">
          {t("PHONE_NUMBER")}
        </ThemedText>
        <View style={styles.phoneRow}>
          <CountryDropdown
            value={phonePrefix}
            onChange={setPhonePrefix}
            placeholder={t("CODE")}
            compact
            options={COUNTRIES.map((c): CountryDropdownOption => {
              const optionValue = c.phonePrefix || c.value;
              const label = c.phonePrefix ? `${c.phonePrefix}` : `${c.value}`;
              return { value: optionValue, label, iso: c.iso };
            })}
          />
          <TextInput
            style={styles.phoneInput}
            value={phoneNumber}
            onChangeText={(text) => {
              setPhoneNumber(normalizePhoneInput(text));
              if (phoneNumberError) setPhoneNumberError(null);
            }}
            keyboardType="phone-pad"
            maxLength={phoneMaxLength ?? 15}
            placeholder={phonePlaceholder ?? t("PHONE_PLACEHOLDER")}
            placeholderTextColor="#6B7280"
            autoComplete="tel"
          />
        </View>
        {phoneNumberError ? (
          <ThemedText style={styles.errorText}>{phoneNumberError}</ThemedText>
        ) : null}

        <View style={{ marginTop: 16 }}>
          <FormButton
            onPress={handleSendCode}
            disabled={!isPhoneValid}
            loading={loading}
            text={t(sendCodeKey)}
          />
        </View>

        <View className="flex-row items-center justify-center mt-4">
          <FormLinkButton text={t("BACK")} onPress={handleCancel} />
        </View>

        {footer}
        <RecaptchaDisclosure />
      </>
    );
  }

  return (
    <>
      <ThemedText className="mb-3 text-sm text-gray-600 text-center">
        {(() => {
          const template = t("PHONE_SIGNUP_CODE_SENT");
          const [before, after = ""] = template.split("{{phone}}");
          return (
            <>
              {before}
              <ThemedText className="font-bold text-gray-800">
                {phonePrefix} {phoneNumber}
              </ThemedText>
              {after}
            </>
          );
        })()}
      </ThemedText>
      <ThemedText className="mb-1 text-sm text-gray-700">
        {t("ENTER_VERIFICATION_CODE")}
      </ThemedText>
      <TextInput
        style={styles.codeInput}
        value={verificationCode}
        onChangeText={(text) => {
          setVerificationCode(text);
          if (verificationCodeError) setVerificationCodeError(null);
        }}
        keyboardType="numeric"
        maxLength={6}
        placeholder="123456"
        placeholderTextColor="#6B7280"
        autoComplete="one-time-code"
        editable={!loading}
      />
      {verificationCodeError ? (
        <ThemedText style={styles.errorText}>{verificationCodeError}</ThemedText>
      ) : null}

      <View style={{ marginTop: 16 }}>
        <FormButton
          onPress={handleVerifyCode}
          disabled={verificationCode.length < 6}
          loading={loading}
          text={t("VERIFY")}
        />
      </View>

      <View className="flex-row items-center justify-center mt-4">
        <TouchableOpacity
          onPress={handleResend}
          disabled={loading || cooldownSec > 0}
          testID="phone-auth-resend"
          style={{ paddingHorizontal: 8 }}
        >
          <ThemedText
            className={`text-sm ${
              cooldownSec > 0 ? "text-gray-400" : "text-blue-600"
            }`}
          >
            {cooldownSec > 0
              ? t("RESEND_IN_SECONDS", { seconds: cooldownSec })
              : t("RESEND")}
          </ThemedText>
        </TouchableOpacity>
        <ThemedText className="mx-2 text-sm text-gray-600">·</ThemedText>
        <TouchableOpacity
          onPress={handleCancel}
          disabled={loading}
          style={{ paddingHorizontal: 8 }}
        >
          <ThemedText className="text-sm text-blue-600">{t("BACK")}</ThemedText>
        </TouchableOpacity>
      </View>

      <RecaptchaDisclosure />
    </>
  );
}

const styles = StyleSheet.create({
  phoneRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    width: "100%",
  },
  phoneInput: {
    flex: 1,
    minWidth: 0,
    height: 44,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 0,
    fontSize: 16,
    backgroundColor: "#fff",
  },
  codeInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 18,
    letterSpacing: 4,
    textAlign: "center",
    backgroundColor: "#fff",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: 6,
  },
});
